import { useEffect, useRef, useState } from 'react';
import { anchorProblem } from './anchors.js';
/** Two consecutive failed checks, not one: a re-render can detach a node for a single frame. */
const LOST_AFTER_FAILURES = 2;
function sameRect(a, b) {
    if (a === b)
        return true;
    if (!a || !b)
        return false;
    return a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height;
}
/**
 * The live viewport rect of `el` while `active`, rAF-throttled. Updates on
 * window `resize`, any `scroll` (capture, so nested scrollers count), a
 * `ResizeObserver` on the target and on `<html>`, and the visual viewport
 * (pinch zoom, the iOS keyboard).
 *
 * Every update re-checks that the anchor is still usable — ignoring the
 * viewport, because the user scrolling the page is allowed and the spotlight
 * simply follows — and calls `onLost` after two consecutive failures.
 */
export function useAnchorRect(el, active, onLost) {
    const [rect, setRect] = useState(() => el && active ? el.getBoundingClientRect() : null);
    const onLostRef = useRef(onLost);
    useEffect(() => {
        onLostRef.current = onLost;
    }, [onLost]);
    useEffect(() => {
        if (!el || !active) {
            setRect(null);
            return;
        }
        let frame = null;
        let failures = 0;
        let lostReported = false;
        const measure = () => {
            frame = null;
            if (anchorProblem(el, { viewport: false }) !== null) {
                failures += 1;
                if (failures >= LOST_AFTER_FAILURES && !lostReported) {
                    lostReported = true;
                    onLostRef.current?.();
                }
                return;
            }
            failures = 0;
            const next = el.getBoundingClientRect();
            setRect((prev) => (sameRect(prev, next) ? prev : next));
        };
        const schedule = () => {
            if (frame !== null)
                return;
            frame = requestAnimationFrame(measure);
        };
        measure();
        window.addEventListener('resize', schedule);
        document.addEventListener('scroll', schedule, true);
        const viewport = window.visualViewport;
        viewport?.addEventListener('resize', schedule);
        viewport?.addEventListener('scroll', schedule);
        let observer = null;
        if (typeof ResizeObserver !== 'undefined') {
            observer = new ResizeObserver(schedule);
            observer.observe(el);
            observer.observe(document.documentElement);
        }
        // Layout that moves the target without resizing anything (a sibling
        // collapsing above it) fires none of the above; a slow poll catches it.
        const poll = setInterval(schedule, 500);
        return () => {
            if (frame !== null)
                cancelAnimationFrame(frame);
            window.removeEventListener('resize', schedule);
            document.removeEventListener('scroll', schedule, true);
            viewport?.removeEventListener('resize', schedule);
            viewport?.removeEventListener('scroll', schedule);
            observer?.disconnect();
            clearInterval(poll);
        };
    }, [el, active]);
    return rect;
}
//# sourceMappingURL=use-anchor-rect.js.map